import React, { useEffect, useMemo, useState } from "react";
import { api, API } from "@/lib/api";
import { Download, Search } from "lucide-react";

const STATUSES = ["", "pending", "eligible", "rewarded", "flagged", "cancelled"];

export default function Referrals() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    const { data } = await api.get(`/referrals${status ? `?status=${status}` : ""}`);
    setItems(data);
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const needle = q.trim().toLowerCase();
    return items.filter((r) => [
      r.referrer_name, r.referrer_phone, r.friend_phone,
      r.promo_code, r.reward_category, r.campaign_name, r.order_id, r.id,
    ].filter(Boolean).some((v) => String(v).toLowerCase().includes(needle)));
  }, [items, q]);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-[#1a1c19] sm:text-3xl">Referrals</h1>
          <p className="text-sm text-[#5c665a]">All friend captures, orders, and rewards. Search and filter below.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <a className="admin-btn-ghost" href={`${API}/exports/referrals.csv`} download data-testid="export-referrals-csv">
            <Download className="h-4 w-4" /> Export CSV
          </a>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="admin-input max-w-xs" data-testid="referrals-status-filter">
            {STATUSES.map((s) => <option key={s || "all"} value={s}>{s || "All statuses"}</option>)}
          </select>
        </div>
      </div>

      <div className="admin-card p-3">
        <div className="flex items-center gap-2 rounded-lg border border-[#e2e8e2] bg-white px-3 py-2">
          <Search className="h-4 w-4 text-[#5c665a]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by customer name, phone, promo code, category, order id…"
            className="flex-1 bg-transparent text-sm outline-none"
            data-testid="referrals-search"
          />
          {q && <button onClick={() => setQ("")} className="text-[11px] font-semibold text-[#c01974]">clear</button>}
          <span className="text-[11px] text-[#5c665a]">{filtered.length} / {items.length}</span>
        </div>
      </div>

      <div className="admin-card overflow-x-auto p-0">
        <table className="min-w-full text-sm">
          <thead className="bg-[#fafcfa] text-left text-xs uppercase tracking-wider text-[#5c665a]">
            <tr>
              <th className="px-4 py-3">Referrer</th>
              <th className="px-4 py-3">Friend</th>
              <th className="px-4 py-3">Promo code</th>
              <th className="px-4 py-3">Category</th>
              <th className="px-4 py-3">Reward</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Created</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#e2e8e2]" data-testid="referrals-table">
            {loading ? (
              <tr><td colSpan={8} className="px-4 py-6 text-center text-[#5c665a]">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-6 text-center text-[#5c665a]">No referrals match your search/filter.</td></tr>
            ) : filtered.map((r) => (
              <tr key={r.id} className="hover:bg-[#fafcfa]">
                <td className="px-4 py-3">
                  <div className="font-semibold">{r.referrer_name}</div>
                  <div className="text-xs text-[#5c665a]">{r.referrer_phone}</div>
                </td>
                <td className="px-4 py-3 text-xs">{r.friend_phone}</td>
                <td className="px-4 py-3 font-mono text-xs">{r.promo_code || "—"}</td>
                <td className="px-4 py-3 text-xs">{r.reward_category || "—"}</td>
                <td className="px-4 py-3 font-semibold">{r.reward_amount ? `KES ${r.reward_amount}` : "—"}</td>
                <td className="px-4 py-3">
                  <StatusBadge s={r.status} />
                </td>
                <td className="px-4 py-3 text-xs text-[#5c665a]">{r.created_at?.slice(0, 10)}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    {r.slug && (
                      <a className="admin-btn-ghost" href={`/r/${r.slug}`} target="_blank" rel="noreferrer" data-testid={`open-landing-${r.slug}`}>
                        Landing
                      </a>
                    )}
                    <button className="admin-btn-ghost" onClick={() => setDetail(r)} data-testid={`view-referral-${r.id}`}>View</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {detail && <DetailDrawer ref={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

export function StatusBadge({ s }) {
  const map = {
    pending: "badge-gray",
    eligible: "badge-amber",
    rewarded: "badge-green",
    flagged: "badge-pink",
    cancelled: "badge-gray",
    scheduled: "badge-amber",
    processing: "badge-amber",
    completed: "badge-green",
    failed: "badge-pink",
  };
  return <span className={map[s] || "badge-gray"}>{s}</span>;
}

function DetailDrawer({ ref: r, onClose }) {
  return (
    <div className="fixed inset-0 z-20 flex justify-end bg-black/30" onClick={onClose}>
      <div className="h-full w-full max-w-md overflow-y-auto bg-white p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-bold">Referral detail</h2>
        <dl className="mt-4 space-y-2 text-sm">
          {[
            ["Referrer", `${r.referrer_name} · ${r.referrer_phone}`],
            ["Friend", r.friend_phone],
            ["Campaign", r.campaign_name],
            ["Promo code", r.promo_code || "—"],
            ["Status", r.status],
            ["Reward category", r.reward_category || "—"],
            ["Reward amount", r.reward_amount ? `KES ${r.reward_amount}` : "—"],
            ["Eligible subtotal", `KES ${r.eligible_subtotal}`],
            ["Order ID", r.order_id || "—"],
            ["Fraud flags", (r.fraud_flags || []).join(", ") || "none"],
            ["Created", r.created_at],
            ["Updated", r.updated_at],
          ].map(([k, v]) => (
            <div key={k} className="grid grid-cols-3 gap-2 border-b border-[#e2e8e2] py-2">
              <dt className="text-xs font-bold uppercase tracking-widest text-[#5c665a]">{k}</dt>
              <dd className="col-span-2 break-all font-mono text-xs">{v}</dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
}
